import React, {useEffect} from 'react'
import {useAuth0} from '@auth0/auth0-react'
import {useDispatch, useSelector} from 'react-redux'
import {
  selectedInfluencerName,
  setInfluencerName,
} from '../../../../app-store/kpGlobalStateSlice'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {
  selectAccountInfo,
  selectActiveLocation,
  selectIsUpgrade,
} from '../../../../app-store/PurchaseLinkSlice'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {Input} from '../../../shared/Input'
import {
  ProductsCatalog,
  ProductsCatalogHeader,
} from '../../../../pages/PurchaseLink/features/PurchaseLinkBuilder/ProductsCatalog'
import {OrderSummary} from '../../../../pages/PurchaseLink/features/PurchaseLinkBuilder/OrderSummary'
import {Button} from '../../../shared/Button'
import {
  StepCardWrapper,
  StepCard,
  FooterRow,
  FooterCenter,
  CatalogContentAreaWrapper,
  CatalogContentArea,
  OrderSummaryColumn,
} from '../AccountSetup.styles'

// Embeds the PurchaseLink flow's catalog + order summary (ProductsCatalog +
// OrderSummary, reused as-is from pages/PurchaseLink/features/
// PurchaseLinkBuilder) as Account Setup's "Catalog Selection" step (STEPS[2]
// in AccountSetupSlice) — an in-wizard step, not a routed page. There is no
// dedicated PurchaseLink route/top-level container; this step is the only
// place PurchaseLinkSlice's UI is rendered. PurchaseLinkSlice itself is
// pre-seeded before landing here, from three different entry points:
//   - a brand-new single-location account: handleSingleLocNext
//     (AccountSetup/index.js) dispatches goNext() from the account-info-setup
//     step, so stepHistory has an entry to go back to.
//   - a brand-new multi-location account: AccountInfo.js's handleOrder/
//     handleOrderForParent, same dispatch(goNext()) pattern.
//   - an existing account's "Create Purchase Link" button (AllAccounts/
//     MyAccounts): jumps straight here via setStep(2), so stepHistory is
//     empty — there's nothing to step back to within the wizard.
//
// Fully self-contained, like every other step: owns its own two-column
// layout (Order Summary sits outside the StepCard/footer, flush against its
// own right edge, unlike every other step's single centered card) via
// CatalogContentAreaWrapper/CatalogContentArea/OrderSummaryColumn. See
// AccountSetup/index.js, which just renders this component directly (via
// STEP_REGISTRY) with no layout knowledge of its own.
export function CatalogSelectionStep({
  onCancel,
  onContinue,
  canContinue,
  isContinueLoading,
}) {
  const dispatch = useDispatch()
  const effectiveRole = useSelector(selectEffectiveRole)
  const influencerName = useSelector(selectedInfluencerName) ?? ''
  const isInfluencerOps = effectiveRole.includes('InfluencerOps')
  const isUpgrade = useSelector(selectIsUpgrade)
  const accountInfo = useSelector(selectAccountInfo)
  const activeLocation = useSelector(selectActiveLocation)
  const {user} = useAuth0()
  const client = useAuthClient()
  const upgradeThryvId = activeLocation?.thryvId || accountInfo?.thryvId
  const influencerUserId = user?.user_metadata?.networkId

  // Upgrade: the account's earlier link already carries the influencer, so
  // pull it from the account's latest completed order instead of re-asking.
  useEffect(() => {
    if (!isInfluencerOps || !isUpgrade || !upgradeThryvId) return undefined
    let cancelled = false
    client(
      `keappartner/fetchKeapCompletedOrder/${encodeURIComponent(
        influencerUserId || 'influencerops',
      )}?thryvId=${encodeURIComponent(upgradeThryvId)}&pageSize=1`,
    )
      .then(res => {
        const name = res?.data?.data?.[0]?.influencer_name
        if (!cancelled && name) dispatch(setInfluencerName(name))
      })
      .catch(() => {}) // best effort: the rep can still type the name
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInfluencerOps, isUpgrade, upgradeThryvId])

  const isContinueEnabled =
    canContinue && (!isInfluencerOps || !!influencerName.trim())

  return (
    <CatalogContentAreaWrapper>
      <CatalogContentArea>
        <ProductsCatalogHeader />

        <StepCardWrapper>
          <StepCard isCatalogStep>
            <ProductsCatalog />
          </StepCard>

          <CatalogSelectionFooter onCancel={onCancel} />
        </StepCardWrapper>
      </CatalogContentArea>

      <OrderSummaryColumn>
        <FooterRow style={{padding: '0 10px 10px', justifyContent: 'flex-end'}}>
          <FooterCenter>
            <Button
              variant="solid-gradiant"
              size="sm"
              onClick={onContinue}
              disabled={!isContinueEnabled}
              isLoading={isContinueLoading}
            >
              Continue &gt;
            </Button>
          </FooterCenter>
        </FooterRow>
        <OrderSummary
          actions={
            isInfluencerOps ? (
              <Input
                id="influencer-name"
                label="Influencer"
                required
                disabled={isContinueLoading}
                value={influencerName}
                onChange={e => dispatch(setInfluencerName(e.target.value))}
                placeholder="Influencer Name"
              />
            ) : undefined
          }
        />
      </OrderSummaryColumn>
    </CatalogContentAreaWrapper>
  )
}

// This step's own footer card — sits as a sibling below StepCard, not
// inside it, so it renders as its own gradient/border card matching
// StepCard's. Owning the JSX here (instead of index.js building it inline)
// is what lets this step's footer be restyled independently to match Figma
// without touching the other steps.
export function CatalogSelectionFooter({onCancel}) {
  return (
    <FooterRow>
      <FooterCenter>
        <Button variant="secondary-gray" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </FooterCenter>
    </FooterRow>
  )
}
